import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Building, Scale, Shield, TrendingUp, Wallet, PiggyBank, Building2, AlertTriangle } from "lucide-react";
import { PeriodSelector, KPICard, IndicatorRow } from "@/components/UIComponents";
import { TrendModal } from "@/components/TrendModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCurrency, formatPercent, MONTHS, CONFIG } from "@/lib/config";
import api from "@/lib/api";

export const Structure = () => {
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(1);
  const [quarter, setQuarter] = useState(1);
  const [view, setView] = useState("mensuel");
  const [selectedDept, setSelectedDept] = useState("GLOBAL");
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [cashflow, setCashflow] = useState(null);
  const [actifsDettes, setActifsDettes] = useState(null);
  
  // Modal de tendance
  const [showTrendModal, setShowTrendModal] = useState(false);
  const [selectedKPI, setSelectedKPI] = useState(null);

  const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { getMonthsForPeriod, aggregateMonthlyData } = await import('@/lib/periodUtils');
        const periods = getMonthsForPeriod(view, year, month, quarter);
        
        const [monthsDataRes, adRes] = await Promise.all([
          Promise.all(periods.map(p => api.getDashboard(p.year, p.month))),
          axios.get(`${API_URL}/api/configuration/actifs-dettes`).then(r => r.data),
        ]);
        
        const dashboard = aggregateMonthlyData(monthsDataRes);
        setDashboardData(dashboard);
        setActifsDettes(adRes);
        
        if (view === "mensuel") {
          const cf = await api.getCashflowBancaire(year, month);
          setCashflow(cf);
        }
      } catch (error) {
        console.error("Error:", error);
      }
      setLoading(false);
    };
    fetchData();
  }, [year, month, quarter, view]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div></div>;
  }

  const globalKpi = dashboardData?.kpis?.GLOBAL || {};
  const profits = globalKpi.profits || {};
  const revenus = globalKpi.revenus || {};

  // Données réelles depuis la configuration Actifs/Dettes
  const assets = actifsDettes?.assets || [];
  const debts = actifsDettes?.debts || [];
  
  // Calculer la valeur nette des actifs (après amortissement)
  const valeursActifsNets = assets.reduce((s, a) => {
    const val = parseFloat(a.purchaseValue || 0);
    const residual = parseFloat(a.residualValue || 0);
    const years = parseInt(a.depreciationYears || 5);
    const age = a.purchaseDate ? Math.max(0, new Date().getFullYear() - new Date(a.purchaseDate).getFullYear()) : 0;
    const amortCumule = Math.min(val - residual, ((val - residual) / years) * age);
    return s + (val - amortCumule);
  }, 0);
  
  const actifsCourants = cashflow?.solde_final || 0;
  const actifsTotaux = actifsCourants + valeursActifsNets;
  const dettesTotales = debts.reduce((s, d) => s + parseFloat(d.currentBalance || 0), 0);
  const paiementsMensuels = debts.reduce((s, d) => s + parseFloat(d.monthlyPayment || 0), 0);
  const passifsCourants = paiementsMensuels * 12; // Dettes à servir dans l'année
  const capitauxPropres = actifsTotaux - dettesTotales;

  // Ratios calculés depuis les vraies données
  const ratioLiquidite = passifsCourants > 0 ? actifsCourants / passifsCourants : 0;
  const ratioEndettement = actifsTotaux > 0 ? (dettesTotales / actifsTotaux * 100) : 0;
  const ratioDettesActifs = actifsTotaux > 0 ? (dettesTotales / actifsTotaux) : 0;
  const ratioReinvestissement = profits.apres_impots > 0 ? 100 : 0; // 100% réinvesti par défaut (pas de dividendes trackés)

  const noData = (revenus.total || 0) === 0 && (globalKpi.depenses?.total || 0) === 0;

  return (
    <div data-testid="structure-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between animate-fade-in">
        <div>
          <h1 className="font-heading text-xl sm:text-3xl font-bold text-slate-900">Structure & Stabilité</h1>
          <p className="text-slate-500 mt-1 text-sm">{MONTHS[month]} {year}</p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <Select value={selectedDept} onValueChange={setSelectedDept}>
            <SelectTrigger className="w-full sm:w-48 bg-white">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="GLOBAL">📊 Tous départements</SelectItem>
              <SelectItem value="PRQC">🎨 {CONFIG.department_names.PRQC}</SelectItem>
              <SelectItem value="LMDS">🏪 {CONFIG.department_names.LMDS}</SelectItem>
              <SelectItem value="LOCATION">🚚 {CONFIG.department_names.LOCATION}</SelectItem>
            </SelectContent>
          </Select>
          <PeriodSelector 
            year={year} 
            setYear={setYear} 
            month={month} 
            setMonth={setMonth}
            view={view}
            setView={setView}
            quarter={quarter}
            setQuarter={setQuarter}
          />
        </div>
      </div>

      {noData && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">Aucune donnée financière pour {MONTHS[month]} {year}</p>
            <p className="text-xs text-amber-600 mt-0.5">Les ratios de rentabilité sont basés sur les transactions QuickBooks (2024-2025). Les ratios de bilan utilisent la configuration Actifs/Dettes.</p>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in delay-100">
        <KPICard
          title="Ratio de Liquidité"
          value={ratioLiquidite.toFixed(2)}
          subtitle="Actifs courants / Passifs courants"
          change={ratioLiquidite >= 1.5 ? "Excellent (≥1.5)" : ratioLiquidite >= 1 ? "Acceptable" : "Attention"}
          changeType={ratioLiquidite >= 1.5 ? 'positive' : ratioLiquidite >= 1 ? 'neutral' : 'negative'}
          icon={Scale}
          color={ratioLiquidite >= 1.5 ? "emerald" : "amber"}
          kpiKey="ratio_liquidite"
          onClick={() => {
            setSelectedKPI({title: "Ratio de Liquidité", kpiKey: "ratio_liquidite", isPercent: false, departement: selectedDept});
            setShowTrendModal(true);
          }}
        />
        <KPICard
          title="Ratio d'Endettement"
          value={formatPercent(ratioEndettement)}
          subtitle="Dettes / Actifs totaux"
          change={ratioEndettement <= 50 ? "Sain (≤50%)" : "Élevé"}
          changeType={ratioEndettement <= 50 ? 'positive' : 'negative'}
          icon={Shield}
          color={ratioEndettement <= 50 ? "emerald" : "red"}
          kpiKey="ratio_endettement"
          onClick={() => {
            setSelectedKPI({title: "Ratio d'Endettement", kpiKey: "ratio_endettement", isPercent: true, departement: selectedDept});
            setShowTrendModal(true);
          }}
        />
        <KPICard
          title="Capitaux Propres"
          value={formatCurrency(capitauxPropres)}
          subtitle="Actifs - Dettes"
          icon={Building}
          color="blue"
          kpiKey="capitaux_propres"
          onClick={() => {
            setSelectedKPI({title: "Capitaux Propres", kpiKey: "capitaux_propres", isPercent: false, departement: selectedDept});
            setShowTrendModal(true);
          }}
        />
        <KPICard
          title="Taux Réinvestissement"
          value={formatPercent(ratioReinvestissement)}
          subtitle="Profits réinvestis"
          icon={TrendingUp}
          color="purple"
          kpiKey="taux_reinvestissement"
          onClick={() => {
            setSelectedKPI({title: "Taux Réinvestissement", kpiKey: "taux_reinvestissement", isPercent: true, departement: selectedDept});
            setShowTrendModal(true);
          }}
        />
      </div>

      {/* Détails structure */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in delay-200">
        {/* Bilan simplifié */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Bilan Simplifié</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-emerald-50 rounded-lg p-4">
                <p className="text-sm font-medium text-emerald-700 mb-3">ACTIFS</p>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-emerald-600">Actifs courants (Cash)</span>
                    <span className="money-value text-emerald-700">{formatCurrency(actifsCourants)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-emerald-600">Équipements & Actifs fixes</span>
                    <span className="money-value text-emerald-700">{formatCurrency(valeursActifsNets)}</span>
                  </div>
                  <div className="border-t border-emerald-200 pt-2 flex justify-between font-medium">
                    <span className="text-emerald-800">Total Actifs</span>
                    <span className="money-value text-emerald-800">{formatCurrency(actifsTotaux)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 rounded-lg p-4">
                <p className="text-sm font-medium text-red-700 mb-3">PASSIFS</p>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-red-600">Passifs courants</span>
                    <span className="money-value text-red-700">{formatCurrency(passifsCourants)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-red-600">Dettes à long terme</span>
                    <span className="money-value text-red-700">{formatCurrency(dettesTotales - passifsCourants)}</span>
                  </div>
                  <div className="border-t border-red-200 pt-2 flex justify-between font-medium">
                    <span className="text-red-800">Total Passifs</span>
                    <span className="money-value text-red-800">{formatCurrency(dettesTotales)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex justify-between font-bold">
                  <span className="text-blue-800">CAPITAUX PROPRES</span>
                  <span className="money-value text-blue-800 text-lg">{formatCurrency(capitauxPropres)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Ratios de structure */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Ratios de Structure</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-slate-600">Ratio de liquidité courante</span>
                  <span className={`font-medium ${ratioLiquidite >= 1.5 ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {ratioLiquidite.toFixed(2)}x
                  </span>
                </div>
                <Progress value={Math.min(ratioLiquidite / 2 * 100, 100)} className="h-2" />
                <p className="text-xs text-slate-400 mt-1">Cible: ≥ 1.5x</p>
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-slate-600">Ratio d'endettement</span>
                  <span className={`font-medium ${ratioEndettement <= 50 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {formatPercent(ratioEndettement)}
                  </span>
                </div>
                <Progress value={ratioEndettement} className={`h-2 ${ratioEndettement > 50 ? '[&>div]:bg-red-500' : ''}`} />
                <p className="text-xs text-slate-400 mt-1">Cible: ≤ 50%</p>
              </div>

              <div 
                className="cursor-pointer hover:bg-slate-50 p-2 rounded transition-colors"
                onClick={() => {
                  setSelectedKPI({title: "Dettes / Actifs", kpiKey: "dettes_actifs", isPercent: false, departement: selectedDept});
                  setShowTrendModal(true);
                }}
              >
                <div className="flex justify-between mb-2">
                  <span className="text-slate-600">Dettes / Actifs</span>
                  <span className="font-medium text-slate-700">{ratioDettesActifs.toFixed(2)}x</span>
                </div>
                <Progress value={ratioDettesActifs * 100} className="h-2" />
                <p className="text-xs text-slate-400 mt-1">Plus bas = mieux</p>
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-slate-600">Taux de réinvestissement</span>
                  <span className="font-medium text-purple-600">{formatPercent(ratioReinvestissement)}</span>
                </div>
                <Progress value={ratioReinvestissement} className="h-2 [&>div]:bg-purple-500" />
                <p className="text-xs text-slate-400 mt-1">Profits réinvestis dans l'entreprise</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modal Tendance */}
      {showTrendModal && selectedKPI && (
        <TrendModal
          open={showTrendModal}
          onClose={() => setShowTrendModal(false)}
          title={selectedKPI.title}
          kpiKey={selectedKPI.kpiKey}
          departement={selectedKPI.departement}
          isPercent={selectedKPI.isPercent}
          chartType={selectedKPI.isPercent ? "line" : "bar"}
        />
      )}
    </div>
  );
};

export default Structure;
